import {deepCopy, getValueByPath, removeValueByPath, setValueByPath} from "../Utils/ObjectUtils.js";
import MutableMixin from "../Components/Mixins/MutableMixin.js";

/**
 * @typedef {{ Added: [], Changed: [], Removed: [] }} ArrayMutation
 */

/**
 * @typedef {Object} MutationPropertyRules
 * @property {boolean} undefinedAsNull=true
 * @property {boolean} excludeNull=true
 * @property {boolean} excludeEmptyString=true
 * @property {boolean} excludeEmptyObject=true
 * @property {boolean} excludeEmptyArrays=true
 */

/**
 * @typedef {Object} MutationProperty
 * @property {string} name
 * @property {MutationPropertyRules} rules Defines the default rules for the observed properties.
 */

/**
 * @typedef {MutationProperty & Object} ObserveProperty
 * @property {function(value: *): *} getCompareValue Returns the value to be used to store and compare.
 * @property {function(newValue: *, initValue: *): boolean} compare Compares the newValue and initValue for changes.
 * @property {string[]|ObserveProperty[]|undefined} observeProperties Defines the properties to observe for changes if type is of object or array.
 * @property {function(item: *, index: int): *} identifier Should return an unique identifier used for array mutations.
 * @property {string[]|MutationProperty[]|undefined} additionalRemovedProperties Additional properties which will be added to ArrayMutation.Removed.
 */

/**
 * @typedef MutationOptions
 * @property {MutationPropertyRules} rules Defines the default rules for the observed properties.
 * @property {function(value: *): *} getCompareValue Returns the value to be used to store and compare.
 * @property {function(newValue: *, initValue: *): boolean} compare Compares the newValue and initValue for changes.
 * @property {string[]|ObserveProperty[]|undefined} observeProperties Defines the properties to observe for changes if type is of object or array.
 * @property {function(item: *, index: int): *} identifier Should return an unique identifier used for array mutations.
 * @property {MutationProperty[]|undefined} additionalRemovedProperties Additional properties which will be added to ArrayMutation.Removed.
 */

/**
 * Returns the object value by defined rules.
 *
 * @param {*} value
 * @param {MutationPropertyRules} rules
 * @return {*}
 */
export function extractObjectValue(value, rules) {
	if(value === undefined) {
		if(rules.undefinedAsNull)
			value = null;
	}

	if(value === null) {
		if(rules.excludeNull) return;
	} else if(value === "") {
		if(rules.excludeEmptyString) return;
	} else if(typeof value === "object" && Object.keys(value).length === 0) {
		if(rules.excludeEmptyObject) return;
	} else if(Array.isArray(value) && value.length === 0) {
		if(rules.excludeEmptyArrays) return;
	}

	return value;
}

/**
 * Extracts the defined properties declared by the ``mutationProperties`` of the object and returns it.
 *
 * @param {{}} object
 * @param {MutationProperty[]|undefined} observeProperties
 * @param {MutationPropertyRules} defaultRules
 * @return {{}}
 */
export function extractObjectProperties(object, observeProperties, defaultRules) {
	const extractedProperties = {};

	if(observeProperties) {
		observeProperties.forEach(property => {
			const value = extractObjectValue(object[property.name], property.rules);
			if(value !== undefined) {
				if(property.observeProperties) {
					if(Array.isArray(value)) {
						const mutation = {
							Added: value.map(v => extractObjectProperties(v, property.observeProperties, defaultRules)).filter(props => Object.keys(props).length > 0),
							Changed: [],
							Removed: []
						};

						const changes = removeEmptyMutations(mutation);
						if(changes)
							extractedProperties[property.name] = changes;
					} else {
						// Only use observed properties
						const props = extractObjectProperties(object[property.name], property.observeProperties, defaultRules);

						// Skip empty items
						if(Object.keys(props).length > 0)
							extractedProperties[property.name] = props;
					}
				} else
					extractedProperties[property.name] = value;
			}
		});
	} else {
		// All properties
		Object.keys(object).forEach(key => {
			const value = extractObjectValue(object[key], defaultRules);
			if(value !== undefined)
				extractedProperties[key] = value;
		});
	}

	return extractedProperties;
}

/**
 * Returns the difference of the two objects of defined properties or null when there is no difference.
 *
 * E.g.:
 * a: { a: 1, b: 2, c: 3 }
 * b: { a: 1, b: "b", c: "c" }
 * diff: { c: "c" }
 *
 * @param {{}} newValue
 * @param {{}} initValue
 * @param {ObserveProperty[]|undefined} observeProperties
 * @param {function} defaultIdentifier
 * @param {MutationPropertyRules} defaultRules
 * @param {MutationProperty[]|undefined} defaultAdditionalRemovedProperties
 * @return {{}|null}
 */
export function getObjectDiff(newValue, initValue, observeProperties, defaultIdentifier, defaultRules, defaultAdditionalRemovedProperties) {
	const diff = {};

	if(observeProperties) {
		observeProperties.forEach(property => {
			if(property.observeProperties) {
				// It is expected that the values are either an object or an array
				if(Array.isArray(newValue[property.name])) {
					const changes = removeEmptyMutations(getArrayMutations(
						newValue[property.name], initValue[property.name], property.observeProperties,
						property.identifier, property.rules, property.additionalRemovedProperties
					));

					if(changes)
						diff[property.name] = changes;
				} else {
					const changes = getObjectDiff(
						newValue[property.name], initValue[property.name], property.observeProperties,
						property.identifier, property.rules, property.additionalRemovedProperties
					);

					if(changes)
						diff[property.name] = changes;
				}
			} else if(newValue[property.name] !== initValue[property.name])
				diff[property.name] = newValue[property.name];
		});
	} else {
		// All properties
		Object.keys(initValue).forEach(key => {
			if(initValue[key] !== newValue[key])
				diff[key] = newValue[key];
		});
	}

	return Object.keys(diff).length > 0 ? diff : null;
}

/**
 * Compares and creates a detailed ArrayMutation object from items and initItems.
 * The identifier is used to get an unique id from the item which then will be used to check for changes or removals.
 * The observeProperties is used to watch for item object changes.
 *
 * The `Added` property will contain new items which don't previously was in initItems.
 * The `Changed` property will contain the difference of items which are different in initItems and have an identifier.
 * The `Removed` property will contain removed item identifiers which are no longer in initItems.
 *
 * An item can be marked as "trash" and will be considered as removed from items and will be inserted to "Removed".
 * This is useful if the items have an intermediate state where the item is marked as trash (and maybe visually
 * represented as deleted, to allow for reverting) but not yet pushed to server.
 *
 * E.g:
 * initItems = [
 *      { "ID": "1", "Name": "Planning", "UsedCount": 94 },
 *      { "ID": "2", "Name": "Ongoing", "UsedCount": 32 },
 *      { "ID": "3", "Name": "Done", "UsedCount": 6 }
 *      { "ID": "4", "Name": "In Progress", "UsedCount": 0 }
 * ]
 * items = [
 *      { "ID": "1", "Name": "Planning", "UsedCount": 94 },
 *      { "ID": "2", "Name": "Going On!", "UsedCount": 32 },
 *      { "Name": "New Entry" }
 *      { "ID": "4", "Name": "In Progress", "UsedCount": 0, __trash: true }
 * ]
 * mutation = {
 *      "Added": [{ "Name": "New Entry" } ],
 *      "Changed": [{ "Name": "Ongoing2", "ID": "2" }],
 *      "Removed": ["3", "4"]
 * }
 *
 * @param {[{}]} items
 * @param {[{}]} initItems
 * @param {ObserveProperty[]|undefined} observeProperties
 * @param {function} defaultIdentifier
 * @param {MutationPropertyRules} defaultRules
 * @param {MutationProperty[]|undefined} defaultAdditionalRemovedProperties
 * @return {ArrayMutation}
 */
export function getArrayMutations(items, initItems, observeProperties, defaultIdentifier, defaultRules, defaultAdditionalRemovedProperties) {
	const mutation = {
		Added: [],
		Changed: [],
		Removed: []
	};

	loop1:
		for(let i = 0, len = initItems.length; i < len; i++) {
			const initItem = initItems[i];
			const initItemId = defaultIdentifier(initItem);

			for(let i2 = 0, len = items.length; i2 < len; i2++) {
				const item = items[i2];
				const itemId = defaultIdentifier(item);

				if(itemId === initItemId) {
					// Identifier found in initial items
					if(item.__trash === true) {
						// Item marked as trash -> Add to `Removed`
						if(defaultAdditionalRemovedProperties !== undefined) {
							const removedInfo = Object.assign({}, extractObjectProperties(item, defaultAdditionalRemovedProperties, defaultRules), { ID: initItemId });
							mutation.Removed.push(removedInfo);
						} else
							mutation.Removed.push(initItemId);
					} else {
						// Check for changes
						const changes = getObjectDiff(
							item, initItem, observeProperties,
							defaultIdentifier, defaultRules, defaultAdditionalRemovedProperties
						);

						if(changes) {
							changes.ID = initItemId;
							mutation.Changed.push(changes);
						}
					}

					continue loop1;
				}
			}

			// Not found in initial items -> Add to `Removed`
			mutation.Removed.push(initItemId);
		}

	// Add all non-empty items to `Added` which don't have a identifier
	items.forEach(item => {
		if(defaultIdentifier(item) === undefined) {
			// Only use observed properties
			const props = extractObjectProperties(item, observeProperties, defaultRules);

			// Skip empty items
			if(Object.keys(props).length > 0)
				mutation.Added.push(props);
		}
	});

	return mutation;
}

/**
 * A little helper function to remove Added, Changed and Removed from the ArrayMutation in case those are empty.
 * If all properties are empty, null will returned.
 *
 * @param {ArrayMutation} mutations
 * @return {ArrayMutation|null}
 */
export function removeEmptyMutations(mutations) {
	if(mutations.Added.length === 0)
		delete mutations.Added;

	if(mutations.Changed.length === 0)
		delete mutations.Changed;

	if(mutations.Removed.length === 0)
		delete mutations.Removed;

	if(Object.keys(mutations).length === 0)
		mutations = null;

	return mutations;
}

/**
 * @param {string[]|ObserveProperty[]|undefined} properties
 * @param {MutationOptions} defaultOptions
 * @return {ObserveProperty[]}
 */
export function normalizeMutationProperties(properties, defaultOptions) {
	return properties ? properties.map(property => {
		if(typeof property === "string") {
			return {
				name: property,
				rules: defaultOptions.rules,
			};
		}

		if(!property.hasOwnProperty("name"))
			throw new Error("Invalid ObserveProperty: Missing name");

		property.rules = Object.assign({}, defaultOptions.rules, property.rules);

		return property;
	}) : properties;
}

/**
 * @param {string[]|ObserveProperty[]|undefined} properties
 * @param {MutationOptions} defaultOptions
 * @return {ObserveProperty[]}
 */
export function normalizeObserveProperties(properties, defaultOptions) {
	return properties ? properties.map(property => {
		if(typeof property === "string") {
			return {
				name: property,
				rules: defaultOptions.rules,
				getCompareValue: defaultOptions.getCompareValue,
				compare: defaultOptions.compare,
				identifier: defaultOptions.identifier,
			};
		}

		if(!property.hasOwnProperty("name"))
			throw new Error("Invalid ObserveProperty: Missing name");

		property = Object.assign({}, defaultOptions, property);

		if(property.observeProperties)
			property.observeProperties = normalizeObserveProperties(property.observeProperties, defaultOptions);

		if(property.additionalRemovedProperties)
			property.additionalRemovedProperties = normalizeMutationProperties(property.additionalRemovedProperties, defaultOptions);

		return property;
	}) : properties;
}

/**
 * @typedef {function(hasChanged:boolean):void} HasChangedFunction
 */
export class NamespaceEventDispatcher {

	constructor() {
		/**
		 * @type {Object.<string, HasChangedFunction[]>}
		 */
		this._callbacks = {};
	}

	/**
	 * Binds an callback to the namespace.
	 *
	 * @param {string} ns
	 * @param {HasChangedFunction} callback
	 */
	on(ns, callback) {
		if(!this._callbacks[ns]) {
			this._callbacks[ns] = [];
			this._callbacks[ns].push(callback);
		} else if(!this._callbacks[ns].includes(callback)) {
			this._callbacks[ns].push(callback);
		}
	}

	/**
	 * Unbinds an callback or namespace.
	 *
	 * @param {string} ns
	 * @param {HasChangedFunction} callback
	 */
	off(ns, callback = undefined) {
		if(this._callbacks[ns]) {
			if(callback) {
				const index = this._callbacks[ns].indexOf(callback);
				if(index > -1) {
					this._callbacks[ns].splice(index, 1);
					if(this._callbacks[ns].length === 0)
						delete this._callbacks[ns];
				}
			} else {
				// Remove all callbacks of namespace
				delete this._callbacks[ns];
			}
		}
	}

	/**
	 * Calls all bound callbacks for the namespace.
	 *
	 * @param {string} ns
	 * @param {function(ns:string):array} args
	 */
	dispatch(ns, args) {
		Object.keys(this._callbacks).forEach(namespace => {
			if(ns.startsWith(namespace))
				this._callbacks[namespace].forEach(callback => callback(...args(namespace)));
		});
	}

}

/**
 * TODO: not tested primitive arrays mutation
 * TODO: not tested object changes
 * TODO: if an element get removed from the DOM, remove all initial and mutated data
 * TODO: memory leak, Map, GC fix with WeakMap
 */
export class MutationAPI {

	constructor() {
		this._registeredElements = new Map();
		this._cachedMutationOptions = new Map();
		this._initialData = {};
		this._mutatedData = {};
		this._hasChangesObservers = new NamespaceEventDispatcher();

		/**
		 *
		 * @type {MutationOptions}
		 */
		this.defaultMutationOptions = {
			/**
			 * Returns the value to be used to store and compare.
			 *
			 * If the value is an object like { ID: 123, Name: "Foobar" } the API will by default store that
			 * value without change as initial, and will also deep compare it to detect changes.
			 * But if for example only the ID is required, the value can be altered with this function.
			 * E.g: { getCompareValue: value => value.ID } Then only the value which this function returns will be used
			 * to store and compare.
			 *
			 * By default, empty or undefined values will converted into null.
			 *
			 * @param value
			 * @return {*}
			 */
			getCompareValue(value) {
				if(value === "" || value === undefined)	return null;
				return value;
			},

			/**
			 * Compares the newValue and initValue for changes.
			 *
			 * @param newValue
			 * @param initValue
			 * @return {boolean}
			 */
			compare(newValue, initValue) {
				if(typeof initValue === "object") initValue = JSON.stringify(initValue);
				if(typeof newValue === "object") newValue = JSON.stringify(newValue);

				return newValue !== initValue;
			},

			/**
			 * Defines the properties to check for changes in an object.
			 *
			 * E.g:
			 *
			 * // This will observe all properties using the default rules:
			 * observeProperties: Infinity
			 *
			 * // This will observe only property Name and Age, using the default rules:
			 * observeProperties: ["Name", "Age"]
			 *
			 * // This will will observe only property Name, Age and City.
			 * // Age will use the custom defined rules.
			 * // Name and City will still use the default rules.
			 * observeProperties: [
			 *     { name: "Name" },
			 *     { name: "Age", rules: { excludeEmptyString: false } },
			 *     "City"
			 * ]
			 *
			 * @param {string[]|ObserveProperty[]|undefined}
			 */
			observeProperties: undefined,

			/**
			 * Default rules.
			 *
			 * @param {MutationPropertyRules}
			 */
			rules: {
				undefinedAsNull: true,
				excludeNull: true,
				excludeEmptyString: true,
				excludeEmptyObject: true,
				excludeEmptyArrays: true
			},

			/**
			 * Default identifier for array mutations.
			 * Should return an unique identifier for an array item.
			 *
			 * @param item
			 * @return {*}
			 */
			identifier: item => item.ID,

			/**
			 * Additional properties from the item which will be added to the ArrayMutation.Removed object.
			 *
			 * @param {MutationProperty[]|undefined}
			 */
			additionalRemovedProperties: undefined
		};
	}

	/**
	 * Registers an MutableElement with an namespace.
	 *
	 * @param {string} ns
	 * @param {MutableMixin} element
	 */
	register(ns, element) {
		if(element instanceof MutableMixin) {
			if(!this._registeredElements.has(ns)) {
				this._registeredElements.set(ns, element);
			} else
				console.error("Unable to register: Namespace is already registered", ns);
		} else
			console.warn("Unable to register: Element is not of MutableMixin", element);
	}

	/**
	 * Unregisters an namespace.
	 * Removes initial and any mutated data.
	 *
	 * @param {string} ns
	 */
	unregister(ns) {
		if(this._registeredElements.has(ns)) {
			this._registeredElements.delete(ns);

			if(this._cachedMutationOptions.has(ns))
				this._cachedMutationOptions.delete(ns);

			// Unbind hasChanged callback
			this._hasChangesObservers.off(ns);

			// Remove initial and any mutated data
			removeValueByPath(ns, this._mutatedData);
			removeValueByPath(ns, this._initialData);
		}
	}

	/**
	 * Checks if the namespace or MutableElement is registered.
	 *
	 * @param {string|MutableMixin} ns_or_element
	 * @return {boolean}
	 */
	isRegistered(ns_or_element) {
		// Find by namespace
		if(typeof ns_or_element === "string")
			return this._registeredElements.has(ns_or_element);

		// Find by input MutableElement
		if(ns_or_element instanceof MutableMixin)
			return this._registeredElements.values().find(element => element === ns_or_element) !== undefined;

		// Invalid argument always returns false
		return false;
	}

	/**
	 * Returns the MutableElement registered by the namespace or undefined if not registered.
	 *
	 * @param {string} ns
	 * @return {MutableMixin|undefined}
	 */
	getRegisteredElement(ns) {
		return this._registeredElements.get(ns);
	}

	/**
	 * Returns the MutationOptions defined for the namespace or the default MutationOptions if not defined.
	 *
	 * @param {string} ns
	 * @return {MutationOptions}
	 */
	getMutationOptions(ns) {
		if(!this._cachedMutationOptions.has(ns)) {
			const options = Object.assign({}, this.defaultMutationOptions, ns && this.isRegistered(ns) ? this.getRegisteredElement(ns).mutationOptions : undefined);
			options.observeProperties = normalizeObserveProperties(options.observeProperties, options);
			options.additionalRemovedProperties = normalizeMutationProperties(options.additionalRemovedProperties, options);
			this._cachedMutationOptions.set(ns, options);
		}

		return this._cachedMutationOptions.get(ns);
	}

	/**
	 * Clears the MutationOptions for the namespace if any.
	 *
	 * @param {string} ns
	 */
	clearMutationOptions(ns) {
		if(!ns) return;
		if(this._cachedMutationOptions.has(ns))
			this._cachedMutationOptions.delete(ns);
	}

	/**
	 * Checks if the namespace has an initial value.
	 *
	 * @param {string} ns
	 * @return {boolean}
	 */
	hasInitialValue(ns) {
		if(!ns) return false;
		return getValueByPath(ns, this._initialData) !== undefined;
	}

	/**
	 * Sets the internal initial value for the namespace.
	 *
	 * @param {string} ns
	 * @param value
	 */
	setInitialValue(ns, value) {
		if(!ns) return;

		const mutationOptions = this.getMutationOptions(ns);

		setValueByPath(ns, deepCopy(mutationOptions.getCompareValue(value)), this._initialData);
	}

	/**
	 * Returns the internal stored initial value of the namespace if defined or undefined otherwise.
	 *
	 * @param {string} ns
	 * @return {*}
	 */
	getInitialValue(ns) {
		if(!ns) return;
		return getValueByPath(ns, this._initialData);
	}

	/**
	 * Will compare the value with the internal stored initial value and updates the changes for the namespace.
	 *
	 * @param {string} ns
	 * @param value
	 */
	updateChanges(ns, value) {
		if(!ns || !this.isRegistered(ns)) return;

		const mutationOptions = this.getMutationOptions(ns);

		const initValue = this.getInitialValue(ns);
		const newValue = deepCopy(mutationOptions.getCompareValue(value));

		if(Array.isArray(newValue)) {
			const mutations = removeEmptyMutations(getArrayMutations(
				newValue, initValue, mutationOptions.observeProperties,
				mutationOptions.identifier, mutationOptions.rules, mutationOptions.additionalRemovedProperties
			));

			if(mutations)
				setValueByPath(ns, mutations, this._mutatedData);
			else
				removeValueByPath(ns, this._mutatedData);
		} else {
			if(mutationOptions.compare(newValue, initValue)) {
				setValueByPath(ns, newValue, this._mutatedData);
			} else {
				removeValueByPath(ns, this._mutatedData);
			}
		}

		this._notifyHasChanges(ns);
	}

	/**
	 * Checks if the namespace has changes or un-/registers an "has changed"-event listener for the namespace if an
	 * callback is defined.
	 *
	 * @param {string} ns
	 * @param {function|undefined} callback
	 * @param {boolean} unbind true to bind the callback as a listener.
	 *                         false to unbind the callback as listener.
	 * @return {boolean|void}
	 */
	hasChanges(ns, callback = undefined, unbind = false) {
		if(!ns) return false;

		if(callback && typeof callback === "function") {
			if(unbind) {
				this._hasChangesObservers.off(ns, callback);
			} else {
				this._hasChangesObservers.on(ns, callback);
				this._notifyHasChanges(ns);
			}
		} else {
			return getValueByPath(ns, this._mutatedData) !== undefined;
		}
	}

	/**
	 * Returns the changes of the namespace if any or undefined otherwise.
	 *
	 * @param {string} ns
	 * @return {*|undefined}
	 */
	getChanges(ns) {
		if(!ns) return undefined;
		const changes = getValueByPath(ns, this._mutatedData);
		if(changes)
			return JSON.parse(JSON.stringify(changes));
		return changes;
	}

	/**
	 * Will call the reset method on all registered MutableElements of the namespace.
	 *
	 * @param {string} ns
	 */
	resetChanges(ns) {
		this._registeredElements.forEach((element, namespace) => {
			if(namespace.startsWith(ns))
				element.reset();
		});
	}

	/**
	 * Will call the saveChanges method on all registered MutableElements of the namespace.
	 *
	 * @param {string} ns
	 * @param {*=} newValue
	 */
	saveChanges(ns, newValue = undefined) {
		this._registeredElements.forEach((element, namespace) => {
			if(namespace.startsWith(ns))
				element.saveChanges(namespace === ns ? newValue : undefined);
		});
	}

	_notifyHasChanges(ns) {
		this._hasChangesObservers.dispatch(ns, (namespace) => [this.hasChanges(namespace)]);
	}

}

export default MutationAPI;

MutationAPI.GlobalInstance = new MutationAPI();

window.MutationAPI = MutationAPI;